export interface ChatExportMessage {
  role: 'user' | 'assistant'
  content: string
  sources?: string[]
  isError?: boolean
}

export interface ChatExportLabels {
  title: string
  exportedAt: string
  userRole: string
  assistantRole: string
  error: string
  sources: string
}

export interface ChatExportFile {
  content: string
  fileName: string
  mimeType: 'text/plain;charset=utf-8'
}

/**
 * `downloadjs` treats a string as a legacy byte string when it contains Latin-1
 * characters. Passing a Blob delegates UTF-8 encoding to the browser and keeps
 * the BOM, umlauts, punctuation and emoji intact.
 */
export const createChatExportBlob = (file: ChatExportFile): Blob => (
  new Blob([file.content], { type: file.mimeType })
)

const pad = (value: number) => String(value).padStart(2, '0')

const fileTimestamp = (date: Date) => (
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  + `_${pad(date.getHours())}-${pad(date.getMinutes())}`
)

const displayTimestamp = (date: Date) => (
  `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`
  + `, ${pad(date.getHours())}:${pad(date.getMinutes())}`
)

/**
 * Creates a plain-text export of exactly the transcript rendered by the receipt chat.
 * The formatter reads only the visible message fields; request context and
 * document/auth/model metadata are never serialized.
 */
export const buildChatExport = (
  messages: ChatExportMessage[],
  labels: ChatExportLabels,
  exportedAt: Date = new Date(),
): ChatExportFile | null => {
  if (messages.length === 0) return null

  const transcript = messages.map(message => {
    const role = message.role === 'user' ? labels.userRole : labels.assistantRole
    const heading = `${role}${message.isError ? ` (${labels.error})` : ''}:`
    const visibleSources = message.sources?.length
      ? `\n${labels.sources}:\n${message.sources.map(source => `- ${source}`).join('\n')}`
      : ''
    return `${heading}\n${message.content}${visibleSources}`
  }).join('\n\n')

  return {
    // UTF-8 BOM keeps umlauts intact in Windows text editors that do not auto-detect UTF-8.
    content: `\uFEFF${labels.title}\n${labels.exportedAt}: ${displayTimestamp(exportedAt)}\n\n${transcript}\n`,
    fileName: `Max-Chatverlauf-${fileTimestamp(exportedAt)}.txt`,
    mimeType: 'text/plain;charset=utf-8',
  }
}
